import React, { Component } from "react";
import { get as _get } from "lodash";
import { FormattedMessage, injectIntl } from "react-intl";
import { connect } from "react-redux";

import { CustomModal, ErrorMessage, showAlertMessage } from "../../../resusableComponents/common/formElements";
import { addAdhocContact } from "../../../redux/services/campaign";
import { updateLoadingState } from "../../../redux/actions/application";

const defaultFormState = {
  firstName: "",
  lastName: "",
  email: "",
  linkedinUrl: "",
};

const defaultErrorState = {
  firstName: "",
  lastName: "",
  email: "",
  linkedinUrl: "",
  apiError: "",
};

class AddContact extends Component {

  constructor(props) {
    super(props);

    this.state = {
      showModal: false,
      form: { ...defaultFormState },
      errors: { ...defaultErrorState },
      isSubmitting: false,
    };
  }

  _openModal = () => {
    this.setState({
      showModal: true,
      form: { ...defaultFormState },
      errors: { ...defaultErrorState },
      isSubmitting: false,
    });
  }

  _closeModal = () => {
    this.setState({
      showModal: false,
      form: { ...defaultFormState },
      errors: { ...defaultErrorState },
      isSubmitting: false,
    });
  }

  _handleChange = (field, value) => {
    this.setState((prevState) => ({
      form: { ...prevState.form, [field]: value },
      errors: { ...prevState.errors, [field]: "", apiError: "" },
    }));
  }

  _isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  _isValidUrl = (url) => {
    try {
      const parsed = new URL(url);
      return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
      return false;
    }
  }

  _validate = () => {
    const { intl } = this.props;
    const { form } = this.state;
    const errors = { ...defaultErrorState };
    let isValid = true;

    if (!(form.firstName || "").trim()) {
      errors.firstName = intl.formatMessage({ id: "error.required_field", defaultMessage: "This field is required." });
      isValid = false;
    }

    if (!(form.lastName || "").trim()) {
      errors.lastName = intl.formatMessage({ id: "error.required_field", defaultMessage: "This field is required." });
      isValid = false;
    }

    if (!(form.email || "").trim()) {
      errors.email = intl.formatMessage({ id: "error.required_field", defaultMessage: "This field is required." });
      isValid = false;
    } else if (!this._isValidEmail(form.email)) {
      errors.email = intl.formatMessage({ id: "error.invalid_email", defaultMessage: "Please enter a valid email address." });
      isValid = false;
    }

    if (!(form.linkedinUrl || "").trim()) {
      errors.linkedinUrl = intl.formatMessage({ id: "error.required_field", defaultMessage: "This field is required." });
      isValid = false;
    } else if (!this._isValidUrl(form.linkedinUrl)) {
      errors.linkedinUrl = intl.formatMessage({ id: "error.invalid_url", defaultMessage: "Please enter a valid URL." });
      isValid = false;
    }

    this.setState({ errors });
    return isValid;
  }

  _handleSubmit = async (e) => {
    e.preventDefault();

    if (!this._validate()) { return; }

    const { updateLoadingState, campaignId, onSuccess } = this.props;
    const { form } = this.state;

    this.setState({ isSubmitting: true, errors: { ...defaultErrorState } });

    try {
      if (typeof updateLoadingState === "function") { updateLoadingState(true); }

      const payload = {
        campaign_id: (campaignId || null),
        first_name: (form.firstName || "").trim(),
        last_name: (form.lastName || "").trim(),
        email: (form.email || "").trim(),
        linkedin_url: (form.linkedinUrl || "").trim(),
      };

      const response = await addAdhocContact(payload);

      if ((response.flag || false) === true) {
        this._closeModal();
        showAlertMessage(_get(response, "message", "Card generated successfully."), "success");
        if (typeof onSuccess === "function") { onSuccess(); }
      } else {
        this.setState((prevState) => ({
          errors: {
            ...prevState.errors,
            apiError: _get(response, "message", "Something went wrong while adding contact."),
          },
        }));
      }

    } catch (err) {
      this.setState((prevState) => ({
        errors: {
          ...prevState.errors,
          apiError: err.message || "Something went wrong while adding contact.",
        },
      }));
    } finally {
      this.setState({ isSubmitting: false });
      if (typeof updateLoadingState === "function") { updateLoadingState(false); }
    }
  }

  _renderModal = () => {
    const { intl } = this.props;
    const { showModal, form, errors, isSubmitting } = this.state;

    if (!showModal) { return null; }

    return (
      <CustomModal
        isOpen={showModal}
        modalTitle={intl.formatMessage({ id: "add_contact.modal.title", defaultMessage: "Add Contact" })}
        onHide={this._closeModal}
        onClose={this._closeModal}
        showCloseButton={true}
      >
        <form onSubmit={this._handleSubmit} noValidate>
          {/* API Error */}

          {/* First Name */}
          <div className="mb-3">
            <label className="form-label text-capitalize" htmlFor="ac-first-name">
              <FormattedMessage id="add_contact.field.first_name" defaultMessage="First Name" />
              <span className="text-danger ms-1">*</span>
            </label>
            <input
              id="ac-first-name"
              type="text"
              className={`form-control${errors.firstName ? " is-invalid" : ""}`}
              value={form.firstName}
              onChange={(e) => this._handleChange("firstName", e.target.value)}
              placeholder={intl.formatMessage({ id: "add_contact.placeholder.first_name", defaultMessage: "Enter first name" })}
            />
            {errors.firstName && (
              <div className="invalid-feedback">{errors.firstName}</div>
            )}
          </div>

          {/* Last Name */}
          <div className="mb-3">
            <label className="form-label text-capitalize" htmlFor="ac-last-name">
              <FormattedMessage id="add_contact.field.last_name" defaultMessage="Last Name" />
              <span className="text-danger ms-1">*</span>
            </label>
            <input
              id="ac-last-name"
              type="text"
              className={`form-control${errors.lastName ? " is-invalid" : ""}`}
              value={form.lastName}
              onChange={(e) => this._handleChange("lastName", e.target.value)}
              placeholder={intl.formatMessage({ id: "add_contact.placeholder.last_name", defaultMessage: "Enter last name" })}
            />
            {errors.lastName && (
              <div className="invalid-feedback">{errors.lastName}</div>
            )}
          </div>

          {/* Email */}
          <div className="mb-3">
            <label className="form-label text-capitalize" htmlFor="ac-email">
              <FormattedMessage id="add_contact.field.email" defaultMessage="Email" />
              <span className="text-danger ms-1">*</span>
            </label>
            <input
              id="ac-email"
              type="email"
              className={`form-control${errors.email ? " is-invalid" : ""}`}
              value={form.email}
              onChange={(e) => this._handleChange("email", e.target.value)}
              placeholder={intl.formatMessage({ id: "add_contact.placeholder.email", defaultMessage: "Enter email address" })}
            />
            {errors.email && (
              <div className="invalid-feedback">{errors.email}</div>
            )}
          </div>

          {/* LinkedIn URL */}
          <div className="mb-4">
            <label className="form-label text-capitalize" htmlFor="ac-linkedin-url">
              <FormattedMessage id="add_contact.field.linkedin_url" defaultMessage="LinkedIn URL" />
              <span className="text-danger ms-1">*</span>
            </label>
            <input
              id="ac-linkedin-url"
              type="url"
              className={`form-control${errors.linkedinUrl ? " is-invalid" : ""}`}
              value={form.linkedinUrl}
              onChange={(e) => this._handleChange("linkedinUrl", e.target.value)}
              placeholder={intl.formatMessage({ id: "add_contact.placeholder.linkedin_url", defaultMessage: "https://www.linkedin.com/in/..." })}
            />
            {errors.linkedinUrl && (
              <div className="invalid-feedback">{errors.linkedinUrl}</div>
            )}
          </div>
          {errors.apiError && (
            <ErrorMessage className="mt-3 mb-3 fw-semibold" message={_get(errors, "apiError", "Something went wrong while adding contact.")} />
          )}

          {/* Footer actions */}
          <div className="pt-4 px-4 mx-n4 border-top d-flex gap-3 justify-content-end">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={this._closeModal}
              disabled={isSubmitting}
            >
              <FormattedMessage id="btn.cancel" defaultMessage="Cancel" />
            </button>
            <button
              type="submit"
              className="btn btn-primary text-capitalize"
              disabled={isSubmitting}
            >
              <FormattedMessage id="btn.submit" defaultMessage="Submit" />
            </button>
          </div>
        </form>
      </CustomModal>
    );
  }

  render() {

    return (
      <>
        <div className="col-lg-auto">
          <button
            id="btn-add-contact"
            className="btn btn-outline-primary waves-effect"
            onClick={() => this._openModal()}
          >
            <FormattedMessage id="add_contact.btn.add_contact" defaultMessage="Add Contact" />
          </button>
        </div>
        {this._renderModal()}
      </>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  updateLoadingState: (payload) => dispatch(updateLoadingState(payload)),
});

export default connect(null, mapDispatchToProps)(injectIntl(AddContact));
